import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { AccessTime, AutoAwesome as AiIcon, BarChart as BarChartIcon, Replay } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getMockExamApiPrefix, getMockExamPortalHomePath, getMockExamResultPath } from '../../../api';
import MockExamQuestionStatsBlock from '../../../components/MockExamQuestionStatsBlock';

const formatResultDate = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const formatPct = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return Math.round(n * 10) / 10;
};

/**
 * Aggregated report for one mock exam: average metrics + per-attempt rows (student portal).
 */
const StudentMockExamExamReport = () => {
  const { mockExamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const submittedResultId = location.state?.resultId ?? location.state?.attemptId ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (submittedResultId) {
      navigate(getMockExamResultPath(submittedResultId, 'review'), { replace: true, state: location.state });
    }
  }, [submittedResultId, navigate, location.state]);

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!mockExamId || submittedResultId) return;
    setLoading(true);
    setError(null);
    try {
      // Practice attempts are the ones staff finish from "Finish Practice". Excluding
      // them made this page 404 / render blank right after a practice run.
      const params = { include_test: 1 };
      const px = getMockExamApiPrefix();
      const res = await api.get(`/${px}/mock-exam/exam/${mockExamId}/results-detail`, { params });
      const data = res.data?.data ?? res.data;
      setPayload(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load exam report');
      toast.error('Failed to load exam report');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [mockExamId, submittedResultId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !payload) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'No data'}
        </Alert>
        <Button variant="contained" onClick={() => navigate(`${getMockExamPortalHomePath()}?tab=result`)}>
          Back to My Results
        </Button>
      </Box>
    );
  }

  const { exam_name, category_name, total_marks, averages, attempts = [] } = payload;
  const avg = averages || {};

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" sx={{ mb: 2 }} onClick={() => navigate(`${getMockExamPortalHomePath()}?tab=result`)}>
        Back to My Results
      </Button>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {exam_name}
          </Typography>
          {category_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {category_name}
            </Typography>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Average across {attempts.length} attempt{attempts.length !== 1 ? 's' : ''}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
            <Box>
              <Tooltip title="Mean of percentage scores (AI-adjusted when available)">
                <Chip
                  size="medium"
                  icon={<BarChartIcon sx={{ fontSize: 18 }} />}
                  label={`Avg ${formatPct(avg.avg_percentage)}%`}
                  color={Number(avg.avg_percentage) >= 50 ? 'success' : 'error'}
                  sx={{ fontWeight: 700 }}
                />
              </Tooltip>
              {total_marks > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Avg marks: {Number(avg.avg_score ?? 0).toFixed(1)} / {Number(total_marks).toFixed(0)}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Average question breakdown
              </Typography>
              <MockExamQuestionStatsBlock stats={avg.question_stats} title="Avg (per attempt)" />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Each attempt
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Question details</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attempts.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {row.attempt_number > 1 && <Replay sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    <Typography variant="body2">#{row.attempt_number}</Typography>
                    {row.is_test_mode && (
                      <Chip size="small" label="PRACTICE" color="info" sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{formatResultDate(row.completed_at)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                    {row.time_taken_min != null
                      ? `${row.time_taken_min} min`
                      : row.time_taken_sec != null
                        ? `${Math.round(Number(row.time_taken_sec) / 60)} min`
                        : 'N/A'}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {row.ai_score != null && row.ai_feedback_status === 'completed' ? (
                      <Tooltip title="AI-adjusted score">
                        <Chip
                          size="small"
                          icon={<AiIcon sx={{ fontSize: 13 }} />}
                          label={`${formatPct(row.ai_percentage ?? (row.total_marks > 0 ? Math.round((row.ai_score / row.total_marks) * 1000) / 10 : row.percentage))}%`}
                          color={(row.ai_percentage ?? row.percentage) >= 50 ? 'success' : 'error'}
                          sx={{ fontWeight: 600 }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label={`${formatPct(row.percentage)}%`}
                        color={row.percentage >= 50 ? 'success' : 'error'}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {row.total_marks != null && row.total_marks > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                        Marks:{' '}
                        {row.ai_feedback_status === 'completed' && row.ai_score != null
                          ? Number(row.ai_score).toFixed(1)
                          : Number(row.score ?? 0).toFixed(1)}{' '}
                        / {Number(row.total_marks).toFixed(0)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <MockExamQuestionStatsBlock stats={row.question_stats} />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(getMockExamResultPath(row.id, 'report'))}
                    sx={{ mr: 1 }}
                  >
                    Report
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => navigate(getMockExamResultPath(row.id, 'review'))}>
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StudentMockExamExamReport;
